import { addIdTokenLocalStorage, getIdTokenLocalStorage, removeIdTokenLocalStorage } from '@sbgsports/vault-sso-auth';

import { startImpersonation, endImpersonation } from '../api/vault/impersonation';
import { stashAdminToken, restoreAdminToken, isImpersonating } from './impersonationToken';

export {
  getImpersonator,
  isImpersonating,
  clearImpersonationStash,
} from './impersonationToken';

/**
 * Logging out while impersonating still ends a session, so the end event is sent before
 * the tokens are dropped - it needs the impersonation token to still be active, since
 * that token's `act` claim is what identifies the session. Best effort: a failure here
 * must not stop the logout.
 */
export const endImpersonationForLogout = async (): Promise<void> => {
  if (!isImpersonating()) return;

  try {
    await endImpersonation();
  } catch (e) {
    console.error('[QA] Failed to log the end of the impersonation session', e);
  }
};

/**
 * Identity lives entirely in the token, so a session is started by swapping the token
 * and reloading - no feature code needs to know impersonation is happening.
 *
 * The reload is required, not cosmetic: utils/sso `useSsoTokenForRequests` reads the
 * token once and closes over it when the axios interceptor is installed, so an
 * already-running app would keep sending the old token.
 */
export const beginImpersonation = async (targetUserId: number, reason?: string): Promise<void> => {
  const adminToken = getIdTokenLocalStorage();
  if (!adminToken) throw Error('No SSO token found to impersonate from');

  const { data: { data: { token } } } = await startImpersonation({ targetUserId, reason });

  stashAdminToken(adminToken);
  addIdTokenLocalStorage(token);
  window.location.reload();
};

/**
 * Tells the API first - the audit trail needs the end event, and it has to be sent
 * while the impersonation token is still the active one, since that token's `act`
 * claim is what identifies the session. A failure there must not strand the admin in
 * the session, so the token is always restored.
 */
export const finishImpersonation = async (): Promise<void> => {
  try {
    await endImpersonation();
  } catch (e) {
    console.error('[QA] Failed to log the end of the impersonation session', e);
  }

  if (restoreAdminToken()) {
    window.location.reload();
    return;
  }

  // Nothing usable to go back to - the stash is gone or has itself expired. Reloading
  // here would land back in the same impersonated session with the banner still up and
  // this button unable to do anything, so drop the session and re-authenticate instead.
  // Login rather than logout: the admin's own SSO session is usually still alive, so
  // this puts them straight back in as themselves.
  removeIdTokenLocalStorage();
  window.location.replace(process.env.REACT_APP_SSO_LOGIN_URL);
};
